import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, RefreshCw, Download, Calendar, Building2, ClockArrowUp, Frown } from 'lucide-react';
import apiService from "../services/apiService";

interface DataStatus {
  last_details_updated: string;
  last_fin_year_end: string;
  last_filing_date: string;
}

interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: number;
  // financial?:FinancialTabProps
}

interface DataUpdatePopupProps {
  isOpen: boolean;
  onClose: () => void;
  companyName: string;
  companyId: string;
  handleUnlock:()=>void;
}

const DataUpdatePopup: React.FC<DataUpdatePopupProps> = ({
  isOpen,
  onClose,
  companyName,
  companyId,
  handleUnlock
}) => {
  const [currentStep, setCurrentStep] = useState<'initial' | 'dataStatus' | 'updateRequired' | 'fetching' | 'completed' | 'requested' | 'cancel'>('initial');
  const [dataStatus, setDataStatus] = useState<DataStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [updateRequired, setUpdateRequired] = useState<boolean | null>(null);

  // Mock API call for data status
  const fetchDataStatus = async () => {
    setIsLoading(true);
    setCurrentStep('fetching');
    // await new Promise(resolve => setTimeout(resolve, 2000));
    try {
      const response = await apiService.post<ApiResponse<any>>(
        `/customers/checkComprehensiveStatus`,
        {
          "customerId": companyId
        }
      );
      if(response.data.status == 'NEW'){
        const mockDataStatus: DataStatus = {
          last_details_updated: response.data.result.data.data_status.last_details_updated,
          last_fin_year_end: response.data.result.data.data_status.last_fin_year_end,
          last_filing_date: response.data.result.data.data_status.last_filing_date
        };
        setDataStatus(mockDataStatus);
        setCurrentStep('dataStatus');
      }else if(response.data.status == 'REQUESTED') {
        setCurrentStep('requested');
      }else if(response.data.status == 'FULFILLED') {
        setCurrentStep('completed');
      }else{
        setCurrentStep('cancel');
      }
      // setCustomerData(response.data);
    } catch (err) {
      setCurrentStep('cancel');
      // setError("something when wrong")
    } finally {
      // setLoading(false)
      setIsLoading(false);
    }
  };

  const handleDataUpdateRequired = (required: boolean) => {
    setUpdateRequired(required);
    setCurrentStep('updateRequired');
    
    if (required) {
      // Fetch data instantly
      fetchComprehensiveData();
    } else {
      // Update comprehensive API and fetch
      updateComprehensiveAPI();
    }
  };

  const fetchComprehensiveData = async () => {
    setIsLoading(true);
    setCurrentStep('fetching');
    
    // Simulate instant data fetch
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsLoading(false);
    setCurrentStep('completed');
  };

  const updateComprehensiveAPI = async () => {
    setIsLoading(true);
    setCurrentStep('fetching');
    try{
      const response = await apiService.post<ApiResponse<any>>(
        `/customers/updateProbeComprehensiveApi`,
        {
          "customerId": companyId
        }
      );
      if (response.data.status == 'UPDATEAPI'){
        setCurrentStep('requested');
      }else{
        setCurrentStep('cancel');
      }

    }catch{
      setCurrentStep('cancel');
    }finally{
      // setCurrentStep('completed');
      setIsLoading(false);
    }
    

    
    // Simulate comprehensive API update and fetch
    // await new Promise(resolve => setTimeout(resolve, 3000));
    
  };

  const resetAndClose = () => {
    setCurrentStep('initial');
    setDataStatus(null);
    setUpdateRequired(null);
    setIsLoading(false);
    onClose();
  };

  const handleUnlockcta = () =>{
    handleUnlock();
    setCurrentStep('initial');
    setDataStatus(null);
    setUpdateRequired(null);
    setIsLoading(false);
    onClose();
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent 
        className="max-w-md bg-white/95 backdrop-blur-sm border-[#d9cba6]/30"
        onPointerDownOutside={e => e.preventDefault()}
        onInteractOutside={e => e.preventDefault()}
      >
        <DialogHeader className="pb-4 border-b border-[#d9cba6]/20">
          <DialogTitle className="flex items-center space-x-2 text-gray-900">
            <Building2 className="h-5 w-5 text-[#8b7355]" />
            <span>{companyName}</span>
          </DialogTitle>
          {/* <p className="text-sm text-gray-600 mt-1">
            
          </p> */}
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Initial Step */}
          {currentStep === 'initial' && (
            <div className="text-center space-y-4">
              <div className="p-4 bg-gradient-to-br from-[#d9cba6]/10 to-[#d9cba6]/5 rounded-lg">
                <RefreshCw className="h-8 w-8 text-[#8b7355] mx-auto mb-2" />
                <p className="text-gray-700 font-medium">Ready to fetch company data</p>
                <p className="text-sm text-gray-500 mt-1">Click below to check data status</p>
              </div>
              <Button 
                onClick={fetchDataStatus}
                className="w-full bg-gradient-to-r from-[#d9cba6] to-[#8b7355] hover:from-[#d9cba6]/90 hover:to-[#8b7355]/90 text-white shadow-sm"
              >
                Check Data Status
              </Button>
            </div>
          )}

          {/* Fetching Step */}
          {currentStep === 'fetching' && (
            <div className="text-center space-y-4">
              <div className="p-6 bg-gradient-to-br from-[#d9cba6]/10 to-[#d9cba6]/5 rounded-lg">
                <Loader2 className="h-8 w-8 text-[#8b7355] mx-auto mb-3 animate-spin" />
                <p className="text-gray-700 font-medium">
                  {updateRequired === true ? 'Fetching latest data...' : 
                   updateRequired === false ? 'Updating comprehensive data...' : 
                   'Checking data status...'}
                </p>
                <p className="text-sm text-gray-500 mt-1">Please wait...</p>
              </div>
            </div>
          )}

          {/* Data Status Step */}
          {currentStep === 'dataStatus' && dataStatus && (
            <div className="space-y-4">
              <Card className="bg-gradient-to-br from-[#d9cba6]/5 to-transparent border-[#d9cba6]/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-gray-900 flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-[#8b7355]" />
                    <span>Current Data Status</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Last Details Updated:</span>
                    <Badge variant="outline" className="bg-[#d9cba6]/10 text-[#8b7355] border-[#d9cba6]/30">
                      {formatDate(dataStatus.last_details_updated)}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Last Financial Year End:</span>
                    <Badge variant="outline" className="bg-[#d9cba6]/10 text-[#8b7355] border-[#d9cba6]/30">
                      {formatDate(dataStatus.last_fin_year_end)}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Last Filing Date:</span>
                    <Badge variant="outline" className="bg-[#d9cba6]/10 text-[#8b7355] border-[#d9cba6]/30">
                      {formatDate(dataStatus.last_filing_date)}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              <div className="text-center">
                <p className="text-gray-700 font-medium mb-3">Data Update Required?</p>
                <div className="flex space-x-3">
                  <Button 
                    onClick={() => handleDataUpdateRequired(true)}
                    className="flex-1 bg-[#cec8b0] hover:bg-[#e5d9bd] text-black"
                  >
                    Yes - Fetch Instantly
                  </Button>
                  <Button 
                    onClick={() => handleDataUpdateRequired(false)}
                    className="flex-1 bg-[#a4cdd4] hover:bg-[#dfeff1] text-black"
                  >
                    No - Update Comprehensive
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Completed Step */}
          {currentStep === 'completed' && (
            <div className="text-center space-y-4">
              <div className="p-6 bg-gradient-to-br from-green-50 to-green-100/50 rounded-lg border border-green-200/50">
                <Download className="h-8 w-8 text-green-600 mx-auto mb-3" />
                <p className="text-green-800 font-medium">Company data successfully fetched.</p>
                {/* <p className="text-sm text-green-600 mt-1">
                  {updateRequired ? 'Latest data fetched instantly' : 'Comprehensive data updated'}
                </p> */}
              </div>
              <Button 
                onClick={handleUnlockcta}
                className="w-full bg-gradient-to-r from-[#d9cba6] to-[#8b7355] hover:from-[#d9cba6]/90 hover:to-[#8b7355]/90 text-white"
              >
                Unlock
              </Button>
            </div>
          )}

          {currentStep === 'requested' && (
            <div className="text-center space-y-4">
              <div className="p-6 bg-gradient-to-br from-red-50 to-red-100/50 rounded-lg border border-green-200/50">
                <ClockArrowUp  className="h-8 w-8 text-red-400 mx-auto mb-3" />
                <p className="text-red-800 font-medium">Fetching company data… This may take a few hours.</p>
                {/* <p className="text-sm text-green-600 mt-1">
                  {updateRequired ? 'Latest data fetched instantly' : 'Comprehensive data updated'}
                </p> */}
              </div>
              <Button 
                onClick={resetAndClose}
                className="w-full bg-gradient-to-r from-[#d9cba6] to-[#8b7355] hover:from-[#d9cba6]/90 hover:to-[#8b7355]/90 text-white"
              >
                Close
              </Button>
            </div>
          )}

          {currentStep === 'cancel' && (
            <div className="text-center space-y-4">
              <div className="p-6 bg-gradient-to-br from-red-50 to-red-100/50 rounded-lg border border-green-200/50">
                <Frown  className="h-8 w-8 text-red-400 mx-auto mb-3" />
                <p className="text-red-800 font-medium">Please try again after time.</p>
                {/* <p className="text-sm text-green-600 mt-1">
                  {updateRequired ? 'Latest data fetched instantly' : 'Comprehensive data updated'}
                </p> */}
              </div>
              <Button 
                onClick={resetAndClose}
                className="w-full bg-gradient-to-r from-[#d9cba6] to-[#8b7355] hover:from-[#d9cba6]/90 hover:to-[#8b7355]/90 text-white"
              >
                Close
              </Button>
            </div>
          )}

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DataUpdatePopup;
